import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  HiOutlineBars3,
  HiOutlineChevronDown,
  HiOutlineHeart,
  HiOutlineMagnifyingGlass,
  HiOutlineShoppingBag,
  HiOutlineXMark,
} from "react-icons/hi2";
import { CartContext } from "./CartContext";
import siteConfig from "../config/siteConfig";
import "../css/header.scss";

const MegaMenu = ({ item, onNavigate }) => (
  <div
    className="mega-menu"
    id={`mega-${item.label.toLowerCase()}`}
    aria-label={`${item.label} menu`}
  >
    <div className="mega-menu__inner">
      <div className="mega-menu__columns">
        {item.columns.map((column) => (
          <section className="mega-menu__column" key={column.title}>
            <h2>{column.title}</h2>
            <ul>
              {column.links.map((link) => (
                <li key={`${column.title}-${link.label}`}>
                  <Link to={link.href} onClick={onNavigate}>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>

      {item.feature && (
        <Link
          className="mega-menu__feature"
          to={item.feature.href}
          onClick={onNavigate}
        >
          <span className="mega-menu__feature-mark" aria-hidden="true">
            Å
          </span>
          <span className="mega-menu__feature-copy">
            <span className="mega-menu__eyebrow">{item.feature.eyebrow}</span>
            <strong>{item.feature.title}</strong>
            <span>{item.feature.description}</span>
            <span className="mega-menu__feature-link">
              {item.feature.linkLabel}
            </span>
          </span>
        </Link>
      )}
    </div>
  </div>
);

function Header() {
  const { cartItems, wishlistItems, cartCount, wishlistCount } =
    useContext(CartContext);
  const navigate = useNavigate();
  const location = useLocation();
  const searchInputRef = useRef(null);
  const [activeMenu, setActiveMenu] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const calculatedCartCount = Array.isArray(cartItems)
    ? cartItems.reduce(
        (total, item) => total + (Number(item.quantity) || 0),
        0
      )
    : 0;
  const safeCartCount = Number.isFinite(cartCount)
    ? cartCount
    : calculatedCartCount;
  const safeWishlistCount = Number.isFinite(wishlistCount)
    ? wishlistCount
    : Array.isArray(wishlistItems)
    ? wishlistItems.length
    : 0;

  const closeNavigation = () => {
    setActiveMenu(null);
    setIsMobileMenuOpen(false);
    setIsSearchOpen(false);
  };

  const scrollToTop = () => {
    closeNavigation();
    if (typeof window !== "undefined" && typeof window.scrollTo === "function") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const openSearch = () => {
    setActiveMenu(null);
    setIsMobileMenuOpen(false);
    setIsSearchOpen(true);
  };

  const toggleMobileMenu = () => {
    setActiveMenu(null);
    setIsSearchOpen(false);
    setIsMobileMenuOpen((isOpen) => !isOpen);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    const trimmedSearch = searchTerm.trim();
    navigate(
      trimmedSearch
        ? `/shop?search=${encodeURIComponent(trimmedSearch)}`
        : "/shop"
    );
    closeNavigation();
    if (typeof window !== "undefined" && typeof window.scrollTo === "function") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleSkipLink = (event) => {
    if (typeof document === "undefined") return;
    const mainContent = document.querySelector("main");
    if (!mainContent) return;

    event.preventDefault();
    mainContent.setAttribute("tabindex", "-1");
    mainContent.focus();
  };

  useEffect(() => {
    setActiveMenu(null);
    setIsMobileMenuOpen(false);
    setIsSearchOpen(false);

    if (typeof window === "undefined" || typeof document === "undefined") {
      return undefined;
    }

    if (location.hash) {
      const targetId = decodeURIComponent(location.hash.slice(1));
      const hashTimer = window.setTimeout(() => {
        document.getElementById(targetId)?.scrollIntoView({ block: "start" });
      }, 0);

      return () => window.clearTimeout(hashTimer);
    }

    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    return undefined;
  }, [location.hash, location.pathname, location.search]);

  useEffect(() => {
    if (isSearchOpen) searchInputRef.current?.focus();
  }, [isSearchOpen]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const handleEscape = (event) => {
      if (event.key === "Escape") closeNavigation();
    };
    document.addEventListener("keydown", handleEscape);

    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const previousOverflow = document.body.style.overflow;

    if (isMobileMenuOpen) document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobileMenuOpen]);

  const hasOpenLayer = Boolean(activeMenu || isSearchOpen || isMobileMenuOpen);

  return (
    <header className="site-header">
      <a className="skip-link" href="#root" onClick={handleSkipLink}>
        Skip to main content
      </a>

      <div className="announcement-bar" aria-label="Store announcements">
        <div className="announcement-bar__inner">
          {siteConfig.announcements.map((announcement) => (
            <Link
              key={announcement.label}
              to={announcement.href}
              className="announcement-bar__item"
              onClick={scrollToTop}
            >
              {announcement.label}
            </Link>
          ))}
        </div>
      </div>

      <div
        className="header-navigation"
        onMouseLeave={() => setActiveMenu(null)}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) {
            setActiveMenu(null);
          }
        }}
      >
        <div className="header-navigation__row">
          <button
            className="header-icon-button header-mobile-toggle"
            type="button"
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-navigation"
            onClick={toggleMobileMenu}
          >
            {isMobileMenuOpen ? (
              <HiOutlineXMark aria-hidden="true" />
            ) : (
              <HiOutlineBars3 aria-hidden="true" />
            )}
          </button>

          <nav className="desktop-navigation" aria-label="Primary navigation">
            {siteConfig.navigation.map((item) =>
              item.columns ? (
                <button
                  className={`desktop-navigation__trigger ${
                    activeMenu?.label === item.label ? "is-active" : ""
                  }`}
                  key={item.label}
                  type="button"
                  aria-expanded={activeMenu?.label === item.label}
                  aria-controls={`mega-${item.label.toLowerCase()}`}
                  onMouseEnter={() => setActiveMenu(item)}
                  onFocus={() => setActiveMenu(item)}
                  onClick={() =>
                    setActiveMenu((currentItem) =>
                      currentItem?.label === item.label ? null : item
                    )
                  }
                >
                  <span>{item.label}</span>
                  <HiOutlineChevronDown aria-hidden="true" />
                </button>
              ) : (
                <Link
                  className="desktop-navigation__link"
                  key={item.label}
                  to={item.href}
                  onMouseEnter={() => setActiveMenu(null)}
                  onFocus={() => setActiveMenu(null)}
                  onClick={scrollToTop}
                >
                  {item.label}
                </Link>
              )
            )}
          </nav>

          <Link
            className="header-wordmark"
            to="/"
            aria-label={siteConfig.logo.ariaLabel}
            onClick={scrollToTop}
          >
            {siteConfig.logo.wordmark}
          </Link>

          <div className="header-actions" aria-label="Store tools">
            <button
              className="header-icon-button"
              type="button"
              aria-label="Search"
              aria-expanded={isSearchOpen}
              aria-controls="site-search"
              onClick={() =>
                isSearchOpen ? setIsSearchOpen(false) : openSearch()
              }
            >
              {isSearchOpen ? (
                <HiOutlineXMark aria-hidden="true" />
              ) : (
                <HiOutlineMagnifyingGlass aria-hidden="true" />
              )}
            </button>

            <Link
              className="header-icon-button header-wishlist-link"
              to="/shop?wishlist=true"
              aria-label={`Wishlist, ${safeWishlistCount} item${
                safeWishlistCount === 1 ? "" : "s"
              }`}
              onClick={scrollToTop}
            >
              <HiOutlineHeart aria-hidden="true" />
              {safeWishlistCount > 0 && (
                <span className="header-count" aria-hidden="true">
                  {safeWishlistCount > 99 ? "99+" : safeWishlistCount}
                </span>
              )}
            </Link>

            <Link
              className="header-icon-button"
              to="/cartpage"
              aria-label={`Shopping bag, ${safeCartCount} item${
                safeCartCount === 1 ? "" : "s"
              }`}
              onClick={scrollToTop}
            >
              <HiOutlineShoppingBag aria-hidden="true" />
              {safeCartCount > 0 && (
                <span className="header-count" aria-hidden="true">
                  {safeCartCount > 99 ? "99+" : safeCartCount}
                </span>
              )}
            </Link>
          </div>
        </div>

        {activeMenu && (
          <MegaMenu item={activeMenu} onNavigate={scrollToTop} />
        )}

        {isSearchOpen && (
          <section className="header-search" id="site-search">
            <form className="header-search__form" onSubmit={handleSearchSubmit}>
              <HiOutlineMagnifyingGlass aria-hidden="true" />
              <label className="visually-hidden" htmlFor="global-search-input">
                Search products
              </label>
              <input
                ref={searchInputRef}
                id="global-search-input"
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="What are you looking for?"
                autoComplete="off"
              />
              <button type="submit">Search</button>
            </form>
            <div className="header-search__suggestions">
              <span>Popular:</span>
              {["Jeans", "T-Shirts", "New arrivals"].map((term) => (
                <button
                  key={term}
                  type="button"
                  onClick={() => {
                    setSearchTerm(term);
                    navigate(`/shop?search=${encodeURIComponent(term)}`);
                    closeNavigation();
                  }}
                >
                  {term}
                </button>
              ))}
            </div>
          </section>
        )}
      </div>

      {isMobileMenuOpen && (
        <nav
          className="mobile-navigation"
          id="mobile-navigation"
          aria-label="Mobile navigation"
        >
          <div className="mobile-navigation__inner">
            {siteConfig.navigation.map((item) =>
              item.columns ? (
                <details key={item.label}>
                  <summary>
                    <span>{item.label}</span>
                    <HiOutlineChevronDown aria-hidden="true" />
                  </summary>
                  <div className="mobile-navigation__group">
                    <Link
                      className="mobile-navigation__all"
                      to={item.href}
                      onClick={scrollToTop}
                    >
                      Shop all {item.label}
                    </Link>
                    {item.columns.map((column) => (
                      <section key={column.title}>
                        <h2>{column.title}</h2>
                        {column.links.map((link) => (
                          <Link
                            key={`${column.title}-${link.label}`}
                            to={link.href}
                            onClick={scrollToTop}
                          >
                            {link.label}
                          </Link>
                        ))}
                      </section>
                    ))}
                  </div>
                </details>
              ) : (
                <Link
                  className="mobile-navigation__direct"
                  key={item.label}
                  to={item.href}
                  onClick={scrollToTop}
                >
                  {item.label}
                </Link>
              )
            )}

            <div className="mobile-navigation__service">
              <span>{siteConfig.currency.label}</span>
              <Link to="/contact" onClick={scrollToTop}>
                Help & contact
              </Link>
            </div>
          </div>
        </nav>
      )}

      {hasOpenLayer && (
        <button
          className="header-backdrop"
          type="button"
          aria-label="Close navigation"
          onClick={closeNavigation}
        />
      )}
    </header>
  );
}

export default Header;
